import { createContext, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import * as Sentry from "@sentry/react";
import { Feature, FeatureCollection } from "geojson";
import { groupBy } from "lodash";

import { selectLayerConfigByKey, useGetImportStatusQuery } from "fond/api";
import { ToolbarConfig } from "fond/map/Toolbar";
import { addImport } from "fond/redux/imports";
import { featureCollection } from "fond/turf";
import { ImportStatus } from "fond/types";
import { removeDrawingProperties } from "fond/utils/geojson";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import MultiLayerEditHandler from "./MultiLayerEditHandler";

/**
 * Context for managing multi-layer editing state.
 * Provides reactive state for isSaving, isDirty, and edit operations.
 */
export const MultiLayerEditContext = createContext<{
  /**
   * The version ID being edited
   */
  versionId: string;
  /**
   * Drawing options including layers, validation, and snap settings
   */
  options: MapboxDrawOptions;
  /**
   * Toolbar configuration for the editing UI
   */
  config: ToolbarConfig;
  /**
   * Whether any layers are currently being saved/imported (reactive)
   */
  isSaving: boolean;
  /**
   * Whether all layer imports completed successfully (reactive)
   */
  allComplete: boolean;
  /**
   * Whether there are unsaved changes (reactive)
   */
  isDirty: boolean;
  /**
   * Set the dirty state
   */
  setIsDirty: (dirty: boolean) => void;
  /**
   * Get current features from draw control
   */
  getFeatures: () => Feature[];
  /**
   * Save all features across layers
   */
  save: () => Promise<unknown[]>;
  /**
   * Reset to initial features
   */
  reset: () => void;
  /**
   * Set the reset confirmation callback
   */
  setResetCallback: (callback: () => void) => void;
  /**
   * Set the getFeatures callback
   */
  setGetFeaturesCallback: (callback: () => Feature[]) => void;
  /**
   * Initial features to load (optional)
   */
  initialFeatures?: FeatureCollection;
  /**
   * Map of layer/sublayer ID to their associated Mapbox style layer IDs.
   * Used for efficiently toggling layer visibility.
   */
  layerStyleIds: Record<string, string[]> | undefined;
  /**
   * Set the layer style IDs mapping (called by MultiLayerEditHandler)
   */
  setLayerStyleIds: (styleIds: Record<string, string[]> | undefined) => void;
}>(undefined!);

interface MultiLayerEditProviderProps {
  children: ReactNode;
  versionId: string;
  options: MapboxDrawOptions;
  config: ToolbarConfig;
  initialFeatures?: FeatureCollection;
}

/**
 * Provider for multi-layer editing functionality.
 * Automatically renders MultiLayerEditHandler when layers are configured.
 *
 * Example:
 * ```tsx
 * <MultiLayerEditProvider versionId={versionId} options={editOptions} config={toolbarConfig}>
 *   <YourComponent />
 * </MultiLayerEditProvider>
 * ```
 */
export const MultiLayerEditProvider: React.FC<MultiLayerEditProviderProps> = ({
  children,
  versionId,
  options,
  config,
  initialFeatures,
}: MultiLayerEditProviderProps) => {
  const dispatch = useAppDispatch();
  const [isDirty, setIsDirty] = useState(false);
  const [layerStyleIds, setLayerStyleIds] = useState<Record<string, string[]> | undefined>(undefined);

  // Callbacks that will be set by MultiLayerEditHandler
  const resetCallbackRef = useRef<(() => void) | null>(null);
  const getFeaturesCallbackRef = useRef<(() => Feature[]) | null>(null);

  // Define the layer keys that we want to edit simultaneously
  // Exclude hidden layers from being saved
  const layerKeys = useMemo(() => options?.editOptions?.allLayers?.filter((layer) => !layer.Hide).map((layer) => layer.ID) || [], [options]);

  // Create a mapping of layerKey to display name
  // Uses LayerConfig directly since it's always available (even when Layer doesn't exist yet)
  const layerDisplayNames = useAppSelector((state) => {
    const names: Record<string, string> = {};

    layerKeys.forEach((layerKey) => {
      const layerConfig = selectLayerConfigByKey(state, { versionId, layerKey });
      names[layerKey] = layerConfig?.Label || layerKey;
    });

    return names;
  });

  // Fetch import status to determine if any layers are currently being saved/imported
  const { data: importStatus } = useGetImportStatusQuery({ versionId });

  // Check if any of our layers are currently being imported (reactive state)
  const isSaving = useMemo(() => {
    if (!importStatus || layerKeys.length === 0) return false;

    const activeSavingStatuses = [
      ImportStatus.STARTING,
      ImportStatus.PENDING_UPLOAD,
      ImportStatus.CONVERTING,
      ImportStatus.IMPORTING,
      ImportStatus.BUILDING_TILES,
    ];

    return importStatus.Layers.some((layer) => layerKeys.includes(layer.LayerKey) && activeSavingStatuses.includes(layer.ImportStatus.Status));
  }, [importStatus, layerKeys]);

  // Check if all layers completed successfully (no errors)
  const allComplete = useMemo(() => {
    if (!importStatus || layerKeys.length === 0) return true;

    return importStatus.Layers.filter((layer) => layerKeys.includes(layer.LayerKey)).every(
      (layer) => !layer.ImportStatus?.Status || layer.ImportStatus.Status === ImportStatus.COMPLETE
    );
  }, [importStatus, layerKeys]);

  /**
   * Get current features from the draw control
   */
  const getFeatures = useCallback(() => {
    if (getFeaturesCallbackRef.current) {
      return getFeaturesCallbackRef.current();
    }
    return [];
  }, []);

  /**
   * Save all features across layers
   */
  const save = useCallback(async () => {
    try {
      const features = getFeatures();
      const featuresByLayer = groupBy(features, (f) => f.properties?.layerId);

      // Save all configured layers (even if empty)
      // This allows saving empty layers for deletion or initial creation
      const promises = layerKeys.map((layerKey) => {
        const layerFeatures = featuresByLayer[layerKey] || [];
        const cleanedCollection = removeDrawingProperties(featureCollection(layerFeatures));
        const file = new File([JSON.stringify(cleanedCollection)], "geojson.json", {
          type: "text/plain",
        });
        return dispatch(
          addImport({
            versionId,
            layerKey,
            displayName: layerDisplayNames[layerKey] || layerKey,
            files: [file],
          })
        );
      });

      const result = await Promise.all(promises);
      setIsDirty(false);
      return result;
    } catch (error) {
      Sentry.captureException(error);
      return Promise.reject(error);
    }
  }, [dispatch, getFeatures, versionId, layerDisplayNames, layerKeys]);

  /**
   * Reset to initial features
   */
  const reset = useCallback(() => {
    if (resetCallbackRef.current) {
      resetCallbackRef.current();
    }
  }, []);

  /**
   * Set the reset callback (called by MultiLayerEditHandler)
   */
  const setResetCallback = useCallback((callback: () => void) => {
    resetCallbackRef.current = callback;
  }, []);

  /**
   * Set the getFeatures callback (called by MultiLayerEditHandler)
   */
  const setGetFeaturesCallback = useCallback((callback: () => Feature[]) => {
    getFeaturesCallbackRef.current = callback;
  }, []);

  const value = useMemo(
    () => ({
      versionId,
      options,
      config,
      isSaving,
      allComplete,
      isDirty,
      setIsDirty,
      getFeatures,
      save,
      reset,
      setResetCallback,
      setGetFeaturesCallback,
      initialFeatures,
      layerStyleIds,
      setLayerStyleIds,
    }),
    [
      versionId,
      options,
      config,
      isSaving,
      allComplete,
      isDirty,
      getFeatures,
      save,
      reset,
      setResetCallback,
      setGetFeaturesCallback,
      initialFeatures,
      layerStyleIds,
    ]
  );

  // Check if there are layers configured for editing
  const hasLayersToEdit = options?.editOptions?.allLayers && options.editOptions.allLayers.length > 0;

  return (
    <MultiLayerEditContext.Provider value={value}>
      {children}
      {hasLayersToEdit && <MultiLayerEditHandler />}
    </MultiLayerEditContext.Provider>
  );
};
